package nl.volleyplanner.web.controller.planner.rulesets_config_v2

import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.SharingStarted
import kotlinx.coroutines.flow.combine
import kotlinx.coroutines.flow.stateIn
import kotlinx.html.InputType
import kotlinx.html.TagConsumer
import kotlinx.html.button
import kotlinx.html.classes
import kotlinx.html.div
import kotlinx.html.fieldSet
import kotlinx.html.id
import kotlinx.html.input
import kotlinx.html.js.div
import kotlinx.html.js.onChangeFunction
import kotlinx.html.js.onClickFunction
import kotlinx.html.label
import kotlinx.html.legend
import kotlinx.html.option
import kotlinx.html.select
import kotlinx.html.style
import nl.volleyplanner.api.planner.GroupConstraint
import nl.volleyplanner.api.planner.Ruleset
import nl.volleyplanner.nevoboservice.extensions.toSlug
import nl.volleyplanner.web.CoroutineManager
import nl.volleyplanner.web.base.HTMLComponent
import nl.volleyplanner.web.base.component
import nl.volleyplanner.web.base.component.ListeningComponent
import nl.volleyplanner.web.base.component.badgeToggle
import nl.volleyplanner.web.base.icon
import nl.volleyplanner.web.controller.planner.rulesets_config.GroupConstraintType
import org.w3c.dom.HTMLElement
import org.w3c.dom.HTMLInputElement
import org.w3c.dom.HTMLSelectElement
import kotlin.collections.get

data class RulesetSectionConfig(
    val amountRequired: Int,
    val primaryGroups: List<GroupConstraint>,
    val alternativeGroups: List<GroupConstraint>
)

class RulesetTemplateEditorSectionComponent(
    val ruleset: Ruleset,
    val sectionTitle: String,
    val taskType: String,
    val primaryGroups: List<GroupConstraint>,
    val alternativeGroups: List<GroupConstraint>,
    val supportedConstraints: Map<GroupConstraintType, List<GroupConstraint>>
) : HTMLComponent() {
    val typeFlow = MutableStateFlow<GroupConstraintType?>(GroupConstraintType.fromConstraintGroups(primaryGroups + alternativeGroups))
    val amountRequiredFlow = MutableStateFlow<Int?>(when (taskType) {
        "Referee" -> ruleset.refereeRequiredAmount
        "ScoreKeeper" -> ruleset.scoreKeeperRequiredAmount
        else -> throw IllegalArgumentException("Invalid task type: $taskType")
    })
    val primaryGroupsFlow = MutableStateFlow(primaryGroups)
    val alternativeGroupsFlow = MutableStateFlow(alternativeGroups)

    val rulesetSectionConfigFlow = combine(typeFlow, amountRequiredFlow, primaryGroupsFlow, alternativeGroupsFlow,
        { type, amountRequired, primaryGroups, alternativeGroups ->

            RulesetSectionConfig(
                amountRequired = type?.let { amountRequired } ?: 0,
                // Build lists in order of provided constraints to ensure ordering is preserved for equality checks
                primaryGroups = type?.let { supportedConstraints.getValue(it).filter { primaryGroups.contains(it) } } ?: emptyList(),
                alternativeGroups = type?.let { supportedConstraints.getValue(it).filter { alternativeGroups.contains(it) } } ?: emptyList(),
            )
        }
    )

    override fun render(consumer: TagConsumer<HTMLElement>): HTMLElement {
        return consumer.div(classes = "bg-light-subtle border rounded p-2 mb-2") {
            fieldSet {
                legend(classes = "h6 mb-2 text-primary") {
                    +sectionTitle
                }

                // Amount
                div(classes = "row mb-1") {
                    label(classes = "col-sm-3 col-form-label col-form-label-sm") {
                        htmlFor = "$taskType-amount-$componentId"
                        +"Aantal"
                    }
                    div(classes = "col-sm-9") {
                        input(classes = "form-control form-control-sm") {
                            type = InputType.number
                            id = "$taskType-amount-$componentId"
                            name = "$taskType-amount"
                            min = "0"
                            max = "999"
                            value = (amountRequiredFlow.value ?: 0).toString()
                            style = "max-width: 4em;"

                            onChangeFunction = { event ->
                                amountRequiredFlow.value = (event.currentTarget!! as HTMLInputElement).value.toIntOrNull()
                            }
                        }
                    }
                }

                // Type
                div(classes = "row mb-3") {
                    label(classes = "col-sm-3 col-form-label col-form-label-sm") {
                        htmlFor = "$taskType-type-$componentId"
                        +"Type"
                    }
                    div(classes = "col-sm-9") {
                        component(ListeningComponent("ConstraintTypeSelect", listOf(amountRequiredFlow)) {
                            select (classes = "form-select form-select-sm") {
                                classes += "vp-task-group-type"
                                style = "max-width: 12em;"
                                attributes["data-vp-task-type"] = taskType
                                required = (amountRequiredFlow.value ?: 0) > 0
                                option {
                                    disabled = true
                                    selected = typeFlow.value == null
                                    value = ""
                                    +"Maak keuze"
                                }
                                GroupConstraintType.entries
                                    .filter { it != GroupConstraintType.Custom }
                                    .filter { supportedConstraints[it]?.isNotEmpty() == true }
                                    .forEach { constraintGroupType ->
                                        option {
                                            selected = constraintGroupType == typeFlow.value
                                            value = constraintGroupType.name
                                            +constraintGroupType.label
                                        }
                                    }
                                onChangeFunction = { event ->
                                    typeFlow.value = (event.currentTarget as HTMLSelectElement).value
                                        .let { GroupConstraintType.valueOf(it) }
                                }
                            }
                        })
                    }
                }

                // Primary Groups
                // TODO: Duplicate code with alternative groups
                component(ListeningComponent("RulesetPrimaryGroups", listOf(typeFlow)) {
                    when(typeFlow.value) {
                        GroupConstraintType.AgeGroup, GroupConstraintType.RefereeLicense -> {
                            primaryGroupsFlow.value = supportedConstraints[typeFlow.value]?.filter { primaryGroups.contains(it) } ?: emptyList()
                        }
                        GroupConstraintType.ParentsOwnTeam -> {
                            require(supportedConstraints[typeFlow.value]!!.size == 1)
                            primaryGroupsFlow.value = supportedConstraints[typeFlow.value]!!
                        }
                        else -> primaryGroupsFlow.value = emptyList()
                    }

                    div(classes = "mb-3") {
                        if (!setOf(GroupConstraintType.AgeGroup, GroupConstraintType.RefereeLicense).contains(typeFlow.value)) {
                            classes += "d-none"
                        }
                        id = "$taskType-primary-groups-$componentId"

                        label(classes = "form-label mb-2") {
                            +"Primaire groepen"
                            div(classes = "text-muted small") {
                                +"Leden met eerste voorkeur voor bijbehorende taken"
                            }
                        }
                        div(classes = "border rounded p-3 bg-white min-height-3rem") {
                            classes += "d-flex flex-wrap gap-2 align-items-start"
                            // Badge toggles will be populated based on type selection

                            supportedConstraints[typeFlow.value]?.forEach { groupConstraint ->
                                badgeToggle(
                                    groupConstraint.groupLabel,
                                    selected = primaryGroups.contains(groupConstraint),
                                    dataAttributes = mapOf(),
                                    onChangeFunction = { isSelected ->
                                        if (isSelected) {
                                            primaryGroupsFlow.value = primaryGroupsFlow.value + groupConstraint
                                        } else {
                                            primaryGroupsFlow.value = primaryGroupsFlow.value - groupConstraint
                                        }
                                    }
                                )
                            }
                        }
                    }
                })
                // Alternative
                component(ListeningComponent("RulesetAlternativeGroups", listOf(typeFlow)) {
                    when(typeFlow.value) {
                        GroupConstraintType.AgeGroup, GroupConstraintType.RefereeLicense -> {
                            alternativeGroupsFlow.value = supportedConstraints[typeFlow.value]?.filter { alternativeGroups.contains(it) } ?: emptyList()
                        }
                        // No parents own team for alternative groups, is added to primary only
                        else -> alternativeGroupsFlow.value = emptyList()
                    }
                    div(classes = "mb-1") {
                        if (!setOf(GroupConstraintType.AgeGroup, GroupConstraintType.RefereeLicense).contains(typeFlow.value)) {
                            classes += "d-none"
                        }
                        id = "$taskType-alternative-groups-$componentId"

                        label(classes = "form-label mb-2") {
                            +"Backup groepen"
                            div(classes = "text-muted small") {
                                +"Indien er niet voldoende leden uit de primaire groep zijn"
                            }
                        }
                        div(classes = "border rounded p-3 bg-white min-height-3rem") {
                            classes += "d-flex flex-wrap gap-2 align-items-start"
                            attributes["data-vp-badge-container"] = "$taskType-alternative"
                            // Badge toggles will be populated based on type selection

                            supportedConstraints[typeFlow.value]?.forEach { groupConstraint ->
                                badgeToggle(
                                    groupConstraint.groupLabel,
                                    selected = alternativeGroups.contains(groupConstraint),
                                    dataAttributes = mapOf(),
                                    onChangeFunction = { isSelected ->
                                        if (isSelected) {
                                            alternativeGroupsFlow.value = alternativeGroupsFlow.value + groupConstraint
                                        } else {
                                            alternativeGroupsFlow.value = alternativeGroupsFlow.value - groupConstraint
                                        }
                                    }
                                )
                            }
                        }
                    }
                })
            }
        }

    }

    override suspend fun getState() = null
}

class RulesetTemplateEditorFormComponent(
    val ruleset: Ruleset,
    val supportedRefereeConstraints: Map<GroupConstraintType, List<GroupConstraint>>,
    val supportedScoreKeeperConstraints: Map<GroupConstraintType, List<GroupConstraint>>
) : HTMLComponent() {
    private val _this = this
    private val refereeEditorComponent = RulesetTemplateEditorSectionComponent(
        ruleset,
        "Scheidsrechters",
        "Referee",
        ruleset.primaryRefereeGroups,
        ruleset.alternativeRefereeGroups,
        supportedRefereeConstraints
    )

    private val scoreKeeperEditorComponent = RulesetTemplateEditorSectionComponent(
        ruleset,
        "Tellers",
        "ScoreKeeper",
        ruleset.primaryScoreKeeperGroups,
        ruleset.alternativeScoreKeeperGroups,
        supportedScoreKeeperConstraints
    )

    private val nameFlow = MutableStateFlow(ruleset.name)
    private val deletedFlow = MutableStateFlow(ruleset.archived)

    val rulesetFlow = combine(nameFlow, deletedFlow, refereeEditorComponent.rulesetSectionConfigFlow, scoreKeeperEditorComponent.rulesetSectionConfigFlow, {
        name, isDeleted, refereeConfig, scoreKeeperConfig -> ruleset.copy(
            identifier = name.toSlug(),
            name = name,
            archived = isDeleted,
            refereeRequiredAmount = refereeConfig.amountRequired,
            primaryRefereeGroups = refereeConfig.primaryGroups,
            alternativeRefereeGroups = refereeConfig.alternativeGroups,
            scoreKeeperRequiredAmount = scoreKeeperConfig.amountRequired,
            primaryScoreKeeperGroups = scoreKeeperConfig.primaryGroups,
            alternativeScoreKeeperGroups = scoreKeeperConfig.alternativeGroups
        )
    }).stateIn(CoroutineManager.RENDER_SCOPE, SharingStarted.Eagerly, ruleset)

    override fun render(consumer: TagConsumer<HTMLElement>): HTMLElement {
        return consumer.div {
            // General Section
            div(classes = "row mb-3") {
                label(classes = "col-auto col-form-label") {
                    htmlFor = "ruleset-name-${ruleset.identifier}"
                    +"Naam"
                }
                div(classes = "col-6") {
                    input(classes = "form-control") {
                        type = InputType.text
                        id = "ruleset-name-${ruleset.identifier}"
                        value = ruleset.name
                        disabled = ruleset.name.isNotBlank()
                        required = true
                        placeholder = "Voer naam in"

                        onChangeFunction = { event ->
                            event.preventDefault()
                            nameFlow.value = (event.currentTarget as HTMLInputElement).value
                        }
                    }
                }
                div(classes = "col-auto") {
                    component(ListeningComponent("DeleteRulesetTemplateButton", listOf(deletedFlow)) {
                        button(classes="btn btn-secondary") {
                            onClickFunction = { event ->
                                event.preventDefault()
                                deletedFlow.value = !deletedFlow.value
                            }

                            if (deletedFlow.value) {
                                icon("bi-rewind-btn")
                                +" Herstel"
                            } else {
                                icon("bi-trash")
                                +" Verwijder"
                            }
                        }
                    }, _this)
                }
            }

            component(refereeEditorComponent)
            component(scoreKeeperEditorComponent)
        }
    }

    override suspend fun getState() = null
}